/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

import { createRoot } from 'react-dom/client';
import { ThemeProvider } from 'foundations-theming/components/ThemeProvider';
import { getInboxConnectPath } from '../utils/Links';
// @ts-expect-error not typed
import { addQueryToUrl } from '../utils/UrlBuilders';
import defer from 'hs-promise-utils/defer';
import serviceTypes from '../constants/serviceTypes';
import { jsx as _jsx } from "react/jsx-runtime";
const OAUTHOR_NAMESPACE_CRM = 'crm.v2';
export const OAUTHOR_NAMESPACE_GMAIL = 'gmail';
export const OAUTHOR_NAMESPACE_GMAIL_WITH_CALENDAR = 'gmail-with-calendar';
let handleMessageCallback;
const _getMessageCallbackHandler = deferred => {
  return message => {
    const origin = message.origin || message.originalEvent && message.originalEvent.origin || '';
    if (origin.indexOf('getsidekick') === -1 && origin.indexOf('hubspot') === -1) {
      return;
    }
    const data = message.data || {};
    const type = data.type || '';
    if (type.indexOf('INBOX_CONNECT') === -1) {
      return;
    }
    if (data.error === true) {
      deferred.reject(data);
    } else {
      deferred.resolve(data);
    }
    if (handleMessageCallback) {
      window.removeEventListener('message', handleMessageCallback);
      handleMessageCallback = null;
    }
  };
};
class ConnectInbox {
  constructor(connectInboxUrl, serviceType, LegalPromptComponent) {
    this.connectInboxUrl = connectInboxUrl;
    this.serviceType = serviceType;
    this.LegalPromptComponent = LegalPromptComponent;
    if ('BroadcastChannel' in window) {
      this.channel = new BroadcastChannel('inbox_connect_channel');
    }
    if (this.connectInboxUrl === null) {
      console.error('"connectInboxUrl" is a required property on the "ConnectInbox" class.');
    }
    if (this.serviceType === null) {
      console.error('"serviceType" is a required property on the "ConnectInbox" class.');
    }
  }
  connect(portalId, requestGroup = 'CRM_INBOX_CONNECT') {
    return this.connectWithOptions({
      requestGroup,
      portalId,
      checkPrivy: false
    });
  }
  baseConnect(appNamespace, newWindow, portalId, isShared = false, urlOptions) {
    newWindow.location.href = this.getConnectInboxUrl(null, appNamespace, portalId, isShared, urlOptions);
    return this.waitForConnectCallback();
  }
  waitForConnectCallback() {
    const deferred = defer();
    handleMessageCallback = _getMessageCallbackHandler(deferred);
    window.addEventListener('message', handleMessageCallback, false);
    const channel = this.channel;
    if (channel) {
      channel.onmessage = e => {
        if (handleMessageCallback) {
          handleMessageCallback(e);
        }
        channel.close();
      };
    }
    return deferred.promise;
  }
  getOauthParams(options) {
    const inboxCreateContext = options.inboxCreateContext ? JSON.parse(options.inboxCreateContext) : {};
    if (this.serviceType === serviceTypes.gmail) {
      return {
        scopeNamespace: this.getGoogleScopeNamespace(inboxCreateContext)
      };
    }
    if (this.serviceType === serviceTypes.outlookGccHigh) {
      return {
        subdomain: 'us'
      };
    }
    return {};
  }
  getGoogleScopeNamespace(inboxCreateContext) {
    if (inboxCreateContext && inboxCreateContext.scopeNamespace) {
      return inboxCreateContext.scopeNamespace;
    }
    if (this.shouldConnectCalendar(inboxCreateContext)) {
      return OAUTHOR_NAMESPACE_GMAIL_WITH_CALENDAR;
    }
    return OAUTHOR_NAMESPACE_GMAIL;
  }

  // TODO: this will eventually be removed and replaced by the scopeNamespace override
  shouldConnectCalendar(inboxCreateContext) {
    return inboxCreateContext && inboxCreateContext.connectCalendar === true;
  }
  getConnectInboxUrl(next, appNamespace, portalId, isShared = false, options = {}) {
    const oAuthorParams = this.getOauthParams(options);
    const inboxConnectParams = Object.assign({
      origin: window.location.origin,
      serviceType: this.serviceType,
      isShared
    }, options);
    if (portalId) {
      oAuthorParams.portalId = portalId;
      inboxConnectParams.portalId = portalId;
    }
    if (next) {
      inboxConnectParams.next = next;
    }
    const inboxConnectUrl = addQueryToUrl(getInboxConnectPath(portalId), inboxConnectParams);
    oAuthorParams.appendToFinal = inboxConnectUrl;
    const finalUrl = addQueryToUrl(`${this.connectInboxUrl}/${appNamespace || OAUTHOR_NAMESPACE_CRM}`, oAuthorParams);
    return finalUrl;
  }
  connectWithOptions({
    requestGroup = 'CRM_INBOX_CONNECT',
    appNamespace,
    apiClient,
    portalId,
    isShared = false,
    useActionRedirect = false,
    reconnectEmail
  }) {
    return new Promise((resolve, reject) => {
      this.maybeShowPrivyPermissions(false, requestGroup, apiClient).then(newWindow => {
        this.baseConnect(appNamespace, newWindow, portalId, isShared, {
          useActionRedirect,
          reconnectEmail
        }).then(resolve, err => {
          if (newWindow.close) {
            newWindow.close();
          }
          reject(err);
        });
      }, reject);
    });
  }

  // Legacy method - returns a component for the legal prompt
  getLegalPromptComponent(props) {
    const {
      allowRequestGroup,
      apiClient,
      onClick
    } = props;
    const Component = this.LegalPromptComponent;
    return /*#__PURE__*/_jsx(Component, Object.assign({
      allowRequestGroup: allowRequestGroup,
      apiClient: apiClient,
      onClick: onClick
    }, props));
  }
  maybeShowPrivyPermissions(checkPrivy, requestGroup, apiClient) {
    return new Promise((resolve, reject) => {
      let newWindow = null;
      const handleReject = error => {
        if (newWindow && newWindow.close) {
          newWindow.close();
        }
        reject(error);
      };

      // For backwards compatibility, this still uses the prompt pattern
      // Users should migrate to using the component directly
      if (typeof this.LegalPromptComponent === 'function') {
        // this is the old promptable function, still call it
        const promptProps = {
          allowRequestGroup: requestGroup,
          apiClient
        };
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);
        const cleanup = () => {
          root.unmount();
          document.body.removeChild(container);
        };
        const handleSuccess = () => {
          cleanup();
          const popup = window.open();
          if (popup) {
            newWindow = popup;
            resolve(popup);
          }
        };
        const handleCancel = error => {
          cleanup();
          handleReject(error);
        };
        const Component = this.LegalPromptComponent;
        root.render( /*#__PURE__*/_jsx(ThemeProvider, {
          children: /*#__PURE__*/_jsx(Component, Object.assign({}, promptProps, {
            onConfirm: handleSuccess,
            onReject: handleCancel
          }))
        }));
      } else {
        // This should be handled by the consumer now
        console.warn('ConnectInbox: maybeShowPrivyPermissions should be replaced with direct component usage');
        if (window.open) {
          const popup = window.open();
          if (popup) {
            resolve(popup);
          }
        }
      }
    });
  }
}
export default ConnectInbox;